/**
 * One scroll listener and one animation frame for every scroll-driven effect on
 * the page, split into a read phase and a write phase so that a frame cannot
 * interleave measurements with the styles it writes from them.
 *
 * Every register call returns its own unregister. The listeners exist only
 * while something is registered, so a boosted navigation away from the page
 * leaves nothing running.
 */

type Apply = (rect: DOMRect, viewportHeight: number) => void;

interface Probe<T> {
  read: (viewportHeight: number) => T;
  write: (reading: T) => void;
}

const scrubs = new Map<number, { element: Element; apply: Apply }>();
const probes = new Map<number, Probe<unknown>>();

let nextId = 0;
let frame = 0;
let listeners: AbortController | null = null;

const run = (): void => {
  frame = 0;
  const viewportHeight = window.innerHeight;

  const rects = new Map<number, DOMRect>();
  for (const [id, scrub] of scrubs) {
    rects.set(id, scrub.element.getBoundingClientRect());
  }
  const readings = new Map<number, unknown>();
  for (const [id, probe] of probes) {
    readings.set(id, probe.read(viewportHeight));
  }

  for (const [id, scrub] of scrubs) {
    const rect = rects.get(id);
    if (rect !== undefined) scrub.apply(rect, viewportHeight);
  }
  for (const [id, probe] of probes) {
    probe.write(readings.get(id));
  }
};

const schedule = (): void => {
  if (!frame) frame = requestAnimationFrame(run);
};

const attach = (): void => {
  if (listeners !== null) return;
  listeners = new AbortController();
  const { signal } = listeners;
  window.addEventListener("scroll", schedule, { passive: true, signal });
  window.addEventListener("resize", schedule, { signal });
  run();
};

const detach = (): void => {
  if (scrubs.size || probes.size) return;
  listeners?.abort();
  listeners = null;
  cancelAnimationFrame(frame);
  frame = 0;
};

export const registerScrub = (element: Element, apply: Apply): (() => void) => {
  const id = nextId++;
  scrubs.set(id, { element, apply });
  attach();
  return () => {
    scrubs.delete(id);
    detach();
  };
};

/**
 * For an effect that measures something other than one fixed element, such as
 * which of several sections intersects the probe line.
 */
export const registerProbe = <T>(probe: Probe<T>): (() => void) => {
  const id = nextId++;
  probes.set(id, probe as Probe<unknown>);
  attach();
  return () => {
    probes.delete(id);
    detach();
  };
};
